import { useState, useEffect, useCallback } from "react";
import { uid } from "../utils";

function parseObjectValue(obj) {
  if (!obj) return [];
  let parsed = obj;
  if (typeof obj === "string") {
    if (!obj.trim()) return [];
    try {
      parsed = JSON.parse(obj);
    } catch {
      return [];
    }
  }
  if (typeof parsed !== "object" || parsed === null) return [];
  return Object.entries(parsed).map(([key, val], index) => ({
    id: `kv-${index}-${String(key).slice(0, 20)}`,
    key,
    value: typeof val === "string" ? val : JSON.stringify(val),
  }));
}

function entriesToObject(list) {
  const filtered = list.filter((row) => row.key.trim() !== "");
  if (!filtered.length) return undefined;
  const obj = {};
  for (const row of filtered) {
    let parsedValue = row.value;
    try {
      parsedValue = JSON.parse(row.value);
    } catch {
      // keep as string
    }
    obj[row.key.trim()] = parsedValue;
  }
  return obj;
}

export function useServiceFormHook(data, onChange) {
  const [kvList, setKvList] = useState(() => parseObjectValue(data.externalGroup));
  const externalGroupKey = JSON.stringify(data.externalGroup ?? {});

  useEffect(() => {
    const fromData = parseObjectValue(data.externalGroup);
    const fromListObj = entriesToObject(kvList);
    const fromDataObj = entriesToObject(fromData);
    if (JSON.stringify(fromDataObj) !== JSON.stringify(fromListObj)) {
      setKvList(fromData);
    }
  }, [externalGroupKey, data.externalGroup, kvList]);

  const syncExternalGroup = useCallback(
    (newList) => {
      setKvList(newList);
      onChange({ ...data, externalGroup: entriesToObject(newList) });
    },
    [data, onChange]
  );

  const addKvRow = useCallback(() => {
    const newList = [...kvList, { id: String(uid()), key: "", value: "" }];
    syncExternalGroup(newList);
  }, [kvList, syncExternalGroup]);

  const removeKvRow = useCallback(
    (id) => {
      const newList = kvList.filter((row) => row.id !== id);
      syncExternalGroup(newList);
    },
    [kvList, syncExternalGroup]
  );

  const updateKvRow = useCallback(
    (id, field, val) => {
      const newList = kvList.map((row) => (row.id === id ? { ...row, [field]: val } : row));
      syncExternalGroup(newList);
    },
    [kvList, syncExternalGroup]
  );

  return { kvList, addKvRow, removeKvRow, updateKvRow };
}
